import 'normalize.css/normalize.css';

import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import Head from 'next/head';
import { Global, css, ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { fetchAndActivate, getRemoteConfig, getString } from 'firebase/remote-config';
import { decode } from 'js-base64';
import { FeatureToggle } from '@projectaltair/cosmos-c-feature-toggle';

import { wrapper } from '@/shared/utils/redux/store';
import OverlayLoading from '@/shared/components/loading/overlay';
import ErrorPopup from '@/shared/components/error-popup';
import theme from '@/assets/styles/theme/index';
import GlobalStyle from '@/assets/styles/global';
import SpaceStyle from '@/assets/styles/space';
import FontStyle from '@/assets/styles/font';
import AlignmentStyle from '@/assets/styles/alignment';
import DimensionStyle from '@/assets/styles/dimension';
import InteractionStyle from '@/assets/styles/interaction';
import { Theme } from '@/assets/styles/theme/type';
import Snackbar from '@/shared/components/snackbar';
import { disableConsole } from '@/core/common/console';
import { configs, RemoteConfigParam } from '@/shared/utils/firebase/remote-config/params';

import packageJson from '../package.json';

/* eslint-disable */
type NextPageWithLayout = NextPage & {
  getLayout?: (page?: ReactElement) => ReactNode;
};
/* eslint-enable */

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

process.env.ENV === 'production' && disableConsole();

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const [maintenanceMode, setMaintenanceMode] = useState('off');

  useEffect(() => {
    try {
      const env = process.env.ENV;
      const configBase64 = process.env.FIREBASE_CONFIG;
      const firebaseConfig = configBase64 ? JSON.parse(decode(configBase64)) : null;
      const app = initializeApp(firebaseConfig);
      const remoteConfig = getRemoteConfig(app);
      remoteConfig.settings.minimumFetchIntervalMillis = env === 'production' ? 1800000 : 0;
      const fetching = async (param: RemoteConfigParam) => {
        try {
          const config = configs[param as keyof typeof configs];
          remoteConfig.defaultConfig = {
            [config.key]: config.defaultValue,
          };
          await fetchAndActivate(remoteConfig);
          const result = getString(remoteConfig, config.key);
          setMaintenanceMode(result);
        } catch (err) {
          throw err;
        }
      };
      fetching('webLinkageMaintenanceMode');
    } catch (error) {}
  }, []);

  return (
    <>
      <Head>
        <link href="https://fonts.cdnfonts.com/css/avenir-lt-pro" rel="stylesheet" />
        <meta name="viewport" content="width=device-width, user-scalable=no" />
      </Head>
      {maintenanceMode === 'on' ? (
        <FeatureToggle mode="maintenance" showFeature={!maintenanceMode}>
          Maintenance Mode
        </FeatureToggle>
      ) : (
        <>
          <Global
            styles={css`
              ${SpaceStyle}
              ${FontStyle}
              ${AlignmentStyle}
              ${DimensionStyle}
              ${InteractionStyle}
            `}
          />
          <ThemeProvider theme={theme}>
            <ErrorPopup />
            <Snackbar />
            {getLayout(
              <>
                <Global
                  styles={(props) => css`
                    ${GlobalStyle(props as Theme)}
                  `}
                />
                <OverlayLoading />
                <Component {...pageProps} />
              </>,
            )}
          </ThemeProvider>
        </>
      )}
    </>
  );
}

export default wrapper.withRedux(MyApp);
